import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface FieldProps {
  label: ReactNode;
  /** Child control; Field injects id / aria-describedby / aria-invalid. */
  children: ReactElement<Record<string, unknown>>;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Optional explicit id (otherwise generated). */
  id?: string;
  className?: string;
  /** Visually hide the label (still announced). */
  hideLabel?: boolean;
  /** Inline action on the label row (e.g. "Tra cứu"). */
  action?: ReactNode;
}

/** Label + control + hint + inline error. Accessible by construction. */
export function Field({ label, children, hint, error, required, id, className, hideLabel, action }: FieldProps) {
  const auto = useId();
  const controlId = id ?? (children.props.id as string | undefined) ?? auto;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const control = isValidElement(children)
    ? cloneElement(children, {
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })
    : children;

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className={cn('flex items-baseline justify-between gap-2', hideLabel && 'sr-only')}>
        <label htmlFor={controlId} className="min-w-0 text-[13px] font-medium text-ink">
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          )}
        </label>
        {action}
      </div>
      {control}
      {hint && !error && (
        <p id={hintId} className="text-xs text-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="flex items-start gap-1 text-xs font-medium text-danger">
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">{error}</span>
        </p>
      )}
    </div>
  );
}
